import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DrfList } from '@shared/models/drf-response.models';
import {
  AdvanceFilterDisplay,
  GeneralMemoParams,
  MemoAcknowledgmentListFilterParams,
  MemoAcknowledgmentListItem,
  MemoAnnouncement,
  MemoAnnouncementPatch,
  MemoAttachment,
  MemoLoaPreview,
  MemoPermission,
  MemoPermissionListParams,
  MemoPermissionPayload,
  MemoRecentSearch,
  MemoRecentSearchParams,
  MemoSavedFilter,
  MemoSavedFilterPayload,
  ModifiedMemoRecentSearch,
} from '@shared/models/memo.model';
import * as _ from 'lodash';
import {
  asyncScheduler,
  lastValueFrom,
  map,
  Observable,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { ApiService } from 'src/app/core/http/api.service';
import { API_URL } from '@shared/service/api.constant';
import { Marker } from '../components/upload-memos/pdf-signature-customizer/pdf-signature-customizer.model';
import {
  BudgetMemoListDetail,
  CommentList,
  DuplicateMemoDetail,
  MemoDetail,
  MemoDetailPatch,
  MemoListDetail,
  ReferencesDetail,
  UploadMemo,
} from '../model/memo.model';
import { UploadMemoPayload } from '../model/template.model';
import { WIDGET_SUBTYPE_KEYS } from './upload-memo.constants';
import { getTransformedMarkerByVersion } from './upload-memo.functions';
import { FolderPermission } from '../../folder/folder.model';
import { DepartmentDisplay } from '@shared/models/user.model';
import { environment } from '../../../../environments/environment';
import { MemoAction } from '@store/approval-request/approval-request.models';
import { AlertService } from '@core/services/alert.service';
import { EFormAttachment } from '../../e-form/shared/eform.model';

@Injectable({
  providedIn: 'root',
})
export class MemoApiService extends ApiService {
  private password: string | null;

  setPassword(accessKey: string | null) {
    this.password = accessKey;
  }

  getPassword() {
    return this.password;
  }

  clearHeaderPassword() {
    this.password = null;
  }

  override requestHeader(language?: string): HttpHeaders {
    return super
      .requestHeader(language)
      .set('Memo-Access-Key', this.password || '');
  }
}

@Injectable({
  providedIn: 'root',
})
export class MemoService {
  baseUrl = environment.baseUrl;

  private setFormat = new Subject();
  inputDateFormat = this.setFormat.asObservable();

  private _getPermissionsParamsCache: Map<
    string,
    Observable<DrfList<MemoPermission>>
  > = new Map();

  constructor(
    protected http: MemoApiService,
    private alert: AlertService,
  ) {}

  setPassword(accessKey: string | null) {
    this.http.setPassword(accessKey);
  }

  getPassword() {
    return this.http.getPassword();
  }

  clearHeaderPassword() {
    this.http.clearHeaderPassword();
  }

  get headers(): HttpHeaders {
    return this.http.requestHeader();
  }

  addPermission(payload: MemoPermissionPayload): Observable<unknown> {
    return this.http.post(API_URL.memo_permissions, payload);
  }

  getNoAckDepartments(
    memoId: number,
  ): Observable<DepartmentDisplay[]> {
    return this.http.get(API_URL.memos + `${memoId}/no-ack-depts/`);
  }

  // My task
  getMyTaskList(params?: { [type: string]: string }): any {
    return this.http.get(API_URL.my_task, params);
  }

  getMyTaskBadge(params?: { [type: string]: string }) {
    return this.http.get<{ count: number }>(
      API_URL.my_task + 'badge/',
      params,
    );
  }

  getGeneralMemoCount(data?: {
    [k: string]: string | null | undefined;
  }): Observable<{ count: number }> {
    return this.http.get(API_URL.general_memo + 'count/', data);
  }

  getWaitingAnnouncementCount(data?: {
    [k: string]: string | null | undefined;
  }): Observable<{ count: number }> {
    return this.http.get(
      API_URL.announcement + 'waiting-count/',
      data,
    );
  }

  // RealMemo
  getGeneralMemoList(
    params?: GeneralMemoParams,
  ): Observable<MemoListDetail> {
    return this.http.get<MemoListDetail>(
      API_URL.general_memo,
      params,
    );
  }

  getBudgetMemoList(
    budgetItemId: number,
    params?: GeneralMemoParams,
  ) {
    return this.http.get<BudgetMemoListDetail>(API_URL.budget_memo, {
      ...params,
      budget_item: budgetItemId,
    });
  }

  getBudgetMemoCSV(
    budgetItemId: number,
    params?: GeneralMemoParams,
  ): Observable<any> {
    return this.http.get(API_URL.budget_memo + 'excel/', {
      ...params,
      budget_item: budgetItemId,
    });
  }

  getMemoListCSV(data: any) {
    // url นี้จะไม่ return file อีกแล้ว แต่จะ return celery task id มาแทน
    return this.http.get(API_URL.general_memo + 'excel/', data);
  }

  getMemoFiles(data: any) {
    return this.http.get(
      API_URL.general_memo + 'download-multiple-pdf/',
      data,
    );
  }

  getMemoLoaPreview(id: number): Observable<MemoLoaPreview[]> {
    return this.http.get(
      API_URL.memos + id.toString() + '/loa-preview/',
    );
  }

  getMemoReferenceById(id: number): Observable<ReferencesDetail[]> {
    return this.http.get<ReferencesDetail[]>(
      API_URL.memos + id + '/references/',
    );
  }

  removeMemoReference(id: number, data: any) {
    return this.http.post(
      API_URL.memos + id + '/remove-references/',
      data,
    );
  }

  removePermission(permissionId: number): Observable<unknown> {
    return this.http.delete(
      API_URL.memo_permissions + permissionId + '/',
    );
  }

  // Action
  createMemo(data: UploadMemoPayload): Observable<MemoDetail> {
    return this.http.post(API_URL.memos, data);
  }

  updateMemo(id: number, data: any): Observable<MemoDetail> {
    return this.http.patch(API_URL.memos + id + '/', data);
  }

  updateAdminCanView(
    id: number,
    isCanView: boolean,
  ): Observable<MemoDetail> {
    return this.http.post(API_URL.memos + id + '/admin-manage/', {
      allow_admin_manage: isCanView,
    });
  }

  updateAnnouncement(
    id: number,
    data: Partial<MemoAnnouncementPatch>,
  ): Observable<MemoAnnouncement> {
    return this.http.post(
      API_URL.memos + id + '/announcement/',
      data,
    );
  }

  publishMemo(id: number, data?: any) {
    return this.http.post(API_URL.memos + id + '/publish/', data);
  }

  extendMemo(id: number, data: any) {
    return this.http.post(API_URL.memos + id + '/extend/', data);
  }

  reviseMemo(id: number) {
    return this.http.post(API_URL.memos + id + '/revise/', {});
  }

  recallMemo(id: number) {
    return this.http.post(API_URL.memos + id + '/recall/', {});
  }

  approveMemo(id: number, data?: any) {
    return this.http.post<MemoDetail>(
      API_URL.memos + id + '/approve/',
      data,
    );
  }

  rejectMemo(id: number, data?: any) {
    return this.http.post(API_URL.memos + id + '/reject/', data);
  }

  terminateMemo(id: number, data?: any) {
    return this.http.post<MemoDetail>(
      API_URL.memos + id + '/terminate/',
      data,
    );
  }

  approveMultiMemos(data?: any): any {
    return this.http.post(API_URL.memos + 'multi-approve/', data);
  }

  rejectMultiMemos(data?: any): any {
    return this.http.post(API_URL.memos + 'multi-reject/', data);
  }

  terminateMultiMemos(data?: any): any {
    return this.http.post(API_URL.memos + 'multi-terminate/', data);
  }

  downloadMemo(id: number): Observable<Blob> {
    return this.http.postResponseBlob(
      API_URL.memos + id + '/download-pdf/',
    );
  }

  printMemo(id: number): any {
    return this.http.post(API_URL.memos + id + '/print/', null);
  }

  getMemoDetail(id: number, params?: any): Observable<MemoDetail> {
    return this.http.get(API_URL.memos + id + '/', params);
  }

  getMemoSignedDocument(
    id: number,
  ): Observable<{ signed_document: string }> {
    return this.http.get(API_URL.memos + id + '/signed-document/');
  }

  getContractDetail(
    memoId: number,
    otp: string,
    ref: string,
  ): Observable<MemoDetail> {
    const headers = this.headers.append(
      'x-requested-with',
      btoa(JSON.stringify({ ref, otp })),
    );
    return this.http.get(API_URL.memos + memoId + '/', {}, headers);
  }

  getMemoHistory(params?: { [type: string]: string }) {
    return this.http.get(API_URL.memos_history, params);
  }

  getPermissions(
    params?: MemoPermissionListParams,
    caching: 'no-cache' | 'cache' = 'no-cache',
  ): Observable<DrfList<MemoPermission>> {
    const paramsString = JSON.stringify(params || {});
    let permissionApiCall =
      this._getPermissionsParamsCache.get(paramsString);
    if (caching === 'cache' && permissionApiCall) {
      return permissionApiCall;
    }
    const source = new Subject<
      MemoPermissionListParams | undefined
    >();
    source.pipe(
      tap({
        subscribe: () => {
          setTimeout(() => {
            // cache for 30s after happening subscribe
            source.complete();
            this._getPermissionsParamsCache.delete(paramsString);
          }, 30000);
        },
      }),
    );
    permissionApiCall = source.pipe(
      startWith(params),
      switchMap((params) =>
        this.http.get<DrfList<MemoPermission>>(
          API_URL.memo_permissions,
          params,
        ),
      ),
      shareReplay(1, undefined, asyncScheduler),
      take(1),
    );
    this._getPermissionsParamsCache.set(
      paramsString,
      permissionApiCall,
    );
    return permissionApiCall;
  }

  getHistoryLogCSV(data: any) {
    return this.http.getBlob(API_URL.memos_history + 'excel/', data);
  }

  deleteMemo(id: any) {
    return this.http.delete(API_URL.memos + id + '/');
  }

  terminateDraftMemo(
    id: any,
    data: { reason: string },
  ): Observable<MemoDetail> {
    return this.http.post(
      API_URL.memos + id + '/terminate-draft/',
      data,
    );
  }

  // Upload Blob

  uploadBlob(id: any, data: any) {
    return this.http.patch(API_URL.upload_memo_blob + id + '/', data);
  }

  uploadContractBlob(id: any, data: any) {
    return this.http.patch(
      API_URL.upload_contract_blob + id + '/',
      data,
    );
  }

  uploadMultiBlob(id: any, data: any) {
    return this.http.patch(
      API_URL.upload_memo_blob_multi + id + '/',
      data,
    );
  }

  uploadContractMultiBlob(id: any, data: any) {
    return this.http.patch(
      API_URL.upload_contract_blob_multi + id + '/',
      data,
    );
  }

  removeBlob(data: any) {
    return this.http.post(API_URL.remove_memo_blob, data);
  }

  removeContractBlob(data: any) {
    return this.http.post(API_URL.remove_contract_blob, data);
  }

  // Comment

  getCommentList(params: any): Observable<CommentList[]> {
    return this.http.get(API_URL.memo_comment, params);
  }

  uploadAttachmentComment(id: number, data: any) {
    return this.http.post(
      API_URL.memo_comment + id + '/bulk-create-attachments/',
      data,
    );
  }

  createNewComment(data: any): Observable<CommentList> {
    return this.http.post(API_URL.memo_comment, data);
  }

  updateMemoRead(data: any) {
    return this.http.post(API_URL.memo_read, data);
  }

  deleteComment(id: any) {
    return this.http.delete(API_URL.memo_comment + id + '/');
  }

  // Attachment
  getMemoAttachment(params?: {
    [type: string]: string;
  }): Observable<MemoAttachment[]> {
    return this.http.get<MemoAttachment[]>(
      API_URL.memo_attachment,
      params,
    );
  }

  removeMemoAttachment(id: any) {
    return this.http.delete(API_URL.memo_attachment + id + '/');
  }

  uploadMemoAttachment(data: any) {
    return this.http.post(API_URL.memo_bulk_attachment, data);
  }

  downloadMemoAttachment(id: any, data?: any) {
    return this.http.postResponseBlob(
      API_URL.memo_attachment + id + '/download/',
      data,
    );
  }

  printFile(url: any): Observable<Blob> {
    return this.http.printFile(url);
  }

  setInputDate(date: any) {
    this.setFormat.next(date);
  }

  getMemoRevised(params: any) {
    return this.http.get(API_URL.memo_revised, params);
  }

  // acknowledgement
  getAcknowledges(
    params?: MemoAcknowledgmentListFilterParams,
  ): Observable<DrfList<MemoAcknowledgmentListItem>> {
    return this.http.get('/api/acknowledges/', params);
  }

  getAcknowledgeCSV(data: MemoAcknowledgmentListFilterParams) {
    return this.http.getBlob<Blob>('/api/acknowledges/excel/', data);
  }

  updateMemoEmailSetting(id: any, data: any) {
    return this.http.post(
      API_URL.memos + id + '/email-setting/',
      data,
    );
  }

  // trash
  getFinishedMemoList(params?: {
    [type: string]: string;
  }): Observable<MemoListDetail> {
    return this.http.get<MemoListDetail>(
      API_URL.finished_memo,
      params,
    );
  }

  getFinishedMemoCount(data?: {
    [k: string]: string | null | undefined;
  }): Observable<{ count: number }> {
    return this.http.get(API_URL.finished_memo + 'count/', data);
  }

  bulkTrashMemo(data: any) {
    return this.http.post(API_URL.bulk_trash_memo, data);
  }

  bulkUntrashMemo(data: any) {
    return this.http.post(API_URL.bulk_untrash_memo, data);
  }

  bulkPermanentlyDeleteMemo(data: any) {
    return this.http.post(API_URL.bulk_permanently_delete_memo, data);
  }

  getFinishedMemoListCSV(data: any) {
    return this.http.get(API_URL.finished_memo + 'excel/', data);
  }

  getFinishedMemoFiles(data: any) {
    return this.http.get(
      API_URL.finished_memo + 'download-multiple-pdf/',
      data,
    );
  }

  exportMemoToGDrive(data: any) {
    return this.http.post<{ task_id: string }>(
      API_URL.finished_memo + 'export-gdrive/',
      data,
    );
  }

  getNdidCredit(isContract = false) {
    if (isContract) {
      return this.http.get('/api/ndid/credit/', {
        memo_type: 'contract',
      });
    }
    return this.http.get('/api/ndid/credit/');
  }

  getRecentSearch(
    params?: MemoRecentSearchParams,
    options = { modifyAdvanceSearch: true },
  ): Observable<MemoRecentSearch[] | ModifiedMemoRecentSearch[]> {
    return this.http
      .get<MemoRecentSearch[]>(
        API_URL.general_memo + 'recent-search/',
        params,
      )
      .pipe(
        map((res) => {
          if (!options.modifyAdvanceSearch) {
            return res;
          }
          return res.map((search) => {
            return {
              ...search,
              advance_filter_display:
                search.advance_filter_display?.reduce(
                  (obj, advSearch) => {
                    obj[advSearch.key] = advSearch;
                    return obj;
                  },
                  {} as {
                    [k: string]: AdvanceFilterDisplay | undefined;
                  },
                ) || {},
            };
          }) as ModifiedMemoRecentSearch[];
        }),
      );
  }

  getContractRecentSearch(
    params?: MemoRecentSearchParams,
    options = { modifyAdvanceSearch: true },
  ): Observable<MemoRecentSearch[] | ModifiedMemoRecentSearch[]> {
    return this.http
      .get<MemoRecentSearch[]>(
        API_URL.contract + 'recent-search/',
        params,
      )
      .pipe(
        map((res) => {
          if (!options.modifyAdvanceSearch) {
            return res;
          }
          return res.map((search) => {
            return {
              ...search,
              advance_filter_display:
                search.advance_filter_display?.reduce(
                  (obj, advSearch) => {
                    obj[advSearch.key] = advSearch;
                    return obj;
                  },
                  {} as {
                    [k: string]: AdvanceFilterDisplay | undefined;
                  },
                ) || {},
            };
          }) as ModifiedMemoRecentSearch[];
        }),
      );
  }

  getThaicomCredit(isContract = false) {
    if (isContract) {
      return this.http.get('/api/thaicom/credit/', {
        memo_type: 'contract',
      });
    }
    return this.http.get('/api/thaicom/credit/');
  }

  /**
   * Remove unnecessary fields in marker for payload's size reduction.
   * This method return new marker without reference pointer.
   */
  refineMarkerForSave(marker: Marker): Marker {
    const clonedMarker = _.cloneDeep(marker);
    delete clonedMarker.apparentPageMaps;
    delete clonedMarker.apparentPageGroups;
    const identityKeys = [...WIDGET_SUBTYPE_KEYS];
    identityKeys.forEach((identityKey) => {
      clonedMarker[identityKey]?.forEach((identity) => {
        delete identity.screenCoordinate;
      });
    });
    return clonedMarker;
  }

  refineLoa(memo: MemoDetail): MemoDetailPatch {
    const memoClone: MemoDetailPatch = {
      ...memo,
      loa_group: memo.loa_group?.id || null,
    };
    return memoClone;
  }

  removeAllMemoAttachment(memo_id: any) {
    return this.http.post('/api/memo-attachments/remove-all/', {
      memo_id,
    });
  }

  addToFolder(
    id: number,
    data: { folder: number | null },
  ): Observable<any> {
    return this.http.post<any>(
      API_URL.memos + id + '/add-to-folder/',
      data,
    );
  }

  updateNote(id: number, data: { note: string }): Observable<any> {
    return this.http.post<any>(API_URL.memos + id + '/note/', data);
  }

  /**
   * Transform markers object in an upload memo by their version.
   * @param data The upload memo or upload memo template that want to transform marker inside.
   * @returns The upload memo or upload memo template that is the same pointer with the argument.
   */
  transformMarkerByVersion(data: UploadMemo): UploadMemo {
    const transformedMarkers = getTransformedMarkerByVersion(
      data.signature_position || [],
      data.widget_version ?? '1.0',
    );
    data.signature_position = transformedMarkers;
    return data;
  }

  sendReminder(id: number, data: any) {
    return this.http.post(
      API_URL.memos + id + '/send-reminder/',
      data,
    );
  }

  expiryDateMemo(id: number, data: any) {
    return this.http.post(API_URL.memos + id + '/expiry-date/', data);
  }

  duplicateMemo(
    id: number,
    data: DuplicateMemoDetail,
  ): Observable<{ id: number; re_init_dynamic_loa: boolean }> {
    return this.http.post(API_URL.memos + id + '/duplicate/', data);
  }

  addMemoReference(id: number, data: { [type: string]: string }) {
    return this.http.post(
      API_URL.memos + id + '/add-references/',
      data,
    );
  }

  updatePermission(
    id: number,
    payload: MemoPermissionPayload,
  ): Observable<unknown> {
    return this.http.patch(
      API_URL.memo_permissions + id + '/',
      payload,
    );
  }

  getFolderMemberViaMemo(
    id: number,
    params: { folder: number },
  ): Observable<FolderPermission[]> {
    return this.http.get(
      API_URL.memos + id + '/folder-members/',
      params,
    );
  }

  uploadEmissionEvidenceFile(id: number, data: any) {
    return this.http.patch(
      '/api/evidence-attachment/' + id + '/',
      data,
    );
  }

  removeEvidenceFile(purchase_id: number) {
    return this.http.delete(
      '/api/evidence-attachment/' + purchase_id + '/',
    );
  }

  getDownloadDetail(id: number) {
    return this.http.get(API_URL.memos + id + '/download-detail/');
  }

  downloadDetail(
    id: number,
    mode: 'merge' | 'split',
    data: any,
  ): Observable<{ task_id: string }> {
    return this.http.post<{ task_id: string }>(
      API_URL.memos + id + '/download-detail/' + mode + '/',
      data,
    );
  }

  //   Password
  setupPassword(id: number, data: any) {
    return this.http.post(
      API_URL.memos + id + '/set-password/',
      data,
    );
  }

  removePassword(id: number, data?: any) {
    return this.http.post(
      API_URL.memos + id + '/remove-password/',
      data,
    );
  }

  forgetPassword(id: number, data?: any) {
    return this.http.post(
      API_URL.memos + id + '/forget-password/',
      data,
    );
  }

  getContractList(params?: {
    [type: string]: string;
  }): Observable<MemoListDetail> {
    return this.http.get<MemoListDetail>(API_URL.contract, params);
  }

  getContractListCSV(data: any) {
    return this.http.get(API_URL.contract + 'excel/', data);
  }

  getContractFiles(data: any) {
    return this.http.get(
      API_URL.contract + 'download-multiple-pdf/',
      data,
    );
  }

  getRequestEvidence(id: any, data: any, header?: HttpHeaders) {
    return this.http.post(
      '/api/memos/contract/' + id + '/request-evidences/',
      data,
      header,
    );
  }

  getSelfAttachment(id: any, data: any, header?: HttpHeaders) {
    return this.http.post(
      '/api/memos/contract/' + id + '/self-request-attachments/',
      data,
      header,
    );
  }

  uploadRequestAttachment(
    id: any,
    data: any,
    header?: HttpHeaders,
  ): Observable<any> {
    return this.http.post(
      '/api/memos/contract/' +
        id +
        '/self-upload-request-attachments/',
      data,
      header,
    );
  }

  uploadSelfieVideo(
    id: any,
    fd: FormData,
    header?: HttpHeaders,
  ): Observable<any> {
    return this.http.post(
      '/api/memos/contract/' + id + '/self-upload-selfie-video/',
      fd,
      header,
    );
  }

  testConvertAndResizeVideo(
    fd: FormData,
    header?: HttpHeaders,
  ): Observable<any> {
    return this.http.postResponseBlob(
      '/api/memos/contract/test-resize-convert-video/',
      fd,
      header,
    );
  }

  getEvidence(id: any) {
    return this.http.get('/api/memos/contract/' + id + '/evidence/');
  }

  getVideoSelfie(id: any, data: any) {
    return this.http.post(
      '/api/memos/contract/' + id + '/selfie-video/',
      data,
    );
  }

  getEvidenceAttachment(id: any, data: any) {
    return this.http.post(
      '/api/memos/contract/' + id + '/request-attachments/',
      data,
    );
  }

  postResubmit(id: any, data: any) {
    return this.http.post(
      '/api/memos/contract/' + id + '/resubmit-request/',
      data,
    );
  }

  confirmEvidence(id: any, data: any) {
    return this.http.post(
      '/api/memos/contract/' + id + '/confirm-evidence/',
      data,
    );
  }

  checkCredit(memoIds: any[], action_type: MemoAction) {
    return this.http.post('/api/memos/contract/check-credit/', {
      memos: memoIds,
      action_type,
    });
  }

  updateMemoCC(id: any, data: any) {
    return this.http.post(API_URL.memos + id + '/update-cc/', data);
  }

  async notifyErrorIfNoContractCredit(
    memoIds: any[],
    action: MemoAction,
  ): Promise<any | null> {
    try {
      await lastValueFrom(this.checkCredit(memoIds, action));
      return null;
    } catch (error: any) {
      if (typeof error.error === 'object') {
        this.alert.error(error.error.detail);
      } else {
        this.alert.error(error.error[0]);
      }
      return error;
    }
  }

  cancelContract(id: any, data: { reason: string }) {
    return this.http.post(
      '/api/memos/contract/' + id + '/cancel/',
      data,
    );
  }

  uploadEFormFiles(
    formData: FormData,
  ): Observable<{ attachments: EFormAttachment[] }> {
    return this.http.post(`/api/upload-eform-attachments/`, formData);
  }

  accessLog(id: number) {
    return this.http.post('api/memos/' + id + '/access-log/', null);
  }

  getSavedFilters(): Observable<DrfList<MemoSavedFilter>> {
    return this.http.get(API_URL.general_memo + 'saved-filter/');
  }

  getSavedFilter(id: number): Observable<MemoSavedFilter> {
    return this.http.get(
      API_URL.general_memo + 'saved-filter/' + id + '/',
    );
  }

  createSavedFilter(
    data: MemoSavedFilterPayload,
  ): Observable<MemoSavedFilter> {
    return this.http.post(
      API_URL.general_memo + 'saved-filter/',
      data,
    );
  }

  editSavedFilter(
    id: number,
    data: MemoSavedFilterPayload,
  ): Observable<MemoSavedFilter> {
    return this.http.patch(
      API_URL.general_memo + 'saved-filter/' + id + '/',
      data,
    );
  }

  deleteSavedFilter(id: number) {
    return this.http.delete(
      API_URL.general_memo + 'saved-filter/' + id + '/',
    );
  }

  getMemoIsNeedEKycToAccess(id: number) {
    return this.http.get<NeedEKycResponse>(
      API_URL.memos + id + '/e-kyc-check/',
    );
  }
}

export interface NeedEKycResponse {
  is_done: boolean;
  need_e_kyc_to_access: 'ndid' | 'thaicom' | null;
}
